import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { UX2, constants } from '@wsb/guac-widget-core';
import dataAids from '../../constants/dataAids';
import getStyles from '../../shop/components/ProductDetail/getStyles';
import { OlsConfigStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import CommonDetails from '../../shop/components/ProductDetail/CommonDetails';
import CheckoutButtons from '../../shop/components/products/details/CheckoutButtons';
import ProductDetailAssets from '../../shop/components/products/ProductDetailAssets';
import ProductLabels from '../../shop/components/products/details/ProductLabels';

function GalleryDetail(props) {
  const {
    Grid,
    Grid: { Cell }
  } = UX2.Component;
  const ref = useRef(null);
  const [imageWidth, setImageWidth] = useState(0);
  const [buyNowEnabled, setBuyNowEnabled] = useState(false);
  useEffect(() => {
    setImageWidth(ref?.current?.offsetWidth || 0);
    setBuyNowEnabled(OlsConfigStore.getState().buyNowEnabled);
  }, []);

  const { Block } = UX2.Element;

  const product = props.product;
  const { isReverse, renderMode, productDetailClick, translate = () => {} } = props;
  const styles = getStyles(props);
  const galleryDetailStyles = {
    ...styles,
    layoutWrapper: {
      position: 'relative'
    },
    grid: {
      'position': 'relative',
      '@sm': {
        alignItems: 'center',
        flexDirection: isReverse ? 'row-reverse' : 'row'
      }
    },
    cellContent: {
      marginBottom: '0'
    },
    labelsContainer: {
      'marginBottom': 0,
      '@xs': {
        display: 'block'
      },
      '@sm': {
        display: 'none'
      }
    },
    name: {
      'marginBottom': 0,
      'textAlign': 'center',
      '@sm': {
        textAlign: 'left'
      }
    },
    smartRibbon: {
      label: {
        marginBottom: 0
      }
    },
    imageWrapper: {
      position: 'relative',
      ['li']: {
        width: `${imageWidth}px`
      }
    },
    ribbon: {
      position: 'absolute',
      top: '10px',
      zIndex: '10'
    },
    buttonWrapper: {
      display: 'flex',
      alignItems: 'center',
      marginTop: 'medium',
      marginBottom: 'large'
    },
    moreLink: {
      'display': 'block',
      'marginVertical': 'small',
      '@sm': {
        marginLeft: 'small',
        marginVertical: '0'
      }
    }
  };

  return (
    <Block style={ galleryDetailStyles.layoutWrapper }>
      <Grid size={ 12 } bottom={ false } spacingSm='large' style={ galleryDetailStyles.grid }>
        <Cell xs={ 12 } sm={ 6 }>
          <Block style={ galleryDetailStyles.imageWrapper }>
            { props.ribbonText && (
              <UX2.Component.Ribbon
                style={ galleryDetailStyles.ribbon }
                dataAids={ `${dataAids.PRODUCT_RIBBON_RENDERED}_${product.name}` }
                ribbonType={ props.ribbonStyle }
              >
                { props.ribbonText }
              </UX2.Component.Ribbon>
            ) }
            <div ref={ ref }>
              <ProductDetailAssets
                renderMode={ renderMode }
                product={ product }
                currentAssetIndex={ props.currentAssetIndex }
                onClickThumbnail={ props.onClickThumbnail }
                assetChangedByVariantSelection={ props.assetChangedByVariantSelection }
                assetsVisible={ 5 }
                fetching={ props.fetching || props.processing || props.inSession }
                pdpImageCropMethod={ props.pdpImageCropMethod }
                pdpImageShape={ props.pdpImageShape }
                pdpLayout={ props.pdpLayout }
                useZoom={ false }
              />
            </div>
          </Block>
        </Cell>
        <Cell xs={ 12 } sm={ 6 } bottom={ false }>
          <UX2.Element.Heading.Middle
            data-aid={ dataAids.PRODUCT_NAME_RENDERED }
            tag='h1'
            children={ product.name }
            style={ galleryDetailStyles.name }
            group='Group'
            featured
          />
          { (product.ship_free || product.limited_edition || product.free_return) && (
            <Block style={ galleryDetailStyles.labelsContainer }>
              <ProductLabels product={ product } translate={ translate } />
            </Block>
          ) }
          <CommonDetails { ...props } showProductLabels isGalleryDetail />
          <Block style={ galleryDetailStyles.buttonWrapper }>
            <CheckoutButtons
              isGalleryDetail
              renderMode={ renderMode }
              prices={ props.determinePrice() }
              { ...props.baseState }
              requiredFieldsBlank={ props.requiredFieldsBlank }
              isSelectedVariantInStock={ props.isSelectedVariantInStock }
              handleAddToCart={ props.handleAddToCart }
              handleApplePayClick={ props.handleApplePayClick }
              getOrderData={ props.getVariantData }
              phoneRequired={ props.stripeConfig.require_phone_number }
              isPaymentRequestEnabled={
                props.stripeConfig.with_payment_request && props.stripeConfig.with_apple_pay
              }
              isPoyntPaymentRequestEnabled={ props.poyntConfig.with_apple_pay }
              translate={ translate }
              hasAddedToCartLabel={ props.hasAddedToCartLabel }
              checkoutStyles={{ display: 'inline-flex', marginBottom: '0' }}
              AdditionalButton={
                (renderMode === constants.renderModes.PUBLISH ||
                  renderMode === constants.renderModes.PREVIEW) &&
                !props.stripeConfig.with_apple_pay &&
                !props.stripeConfig.with_payment_request &&
                !buyNowEnabled ? (
                    <UX2.Element.Button.Spotlight
                      onClick={ productDetailClick }
                      children={ props.actionLinkText || translate('products.view_full_details') }
                      style={{ marginLeft: 'small' }}
                    />
                  ) : (
                    <UX2.Element.MoreLink
                      href='javascript:;'
                      iconSize='1.2em'
                      children={ props.actionLinkText || translate('products.view_full_details') }
                      onClick={ productDetailClick }
                      style={ galleryDetailStyles.moreLink }
                    />
                  )
              }
            />
          </Block>
        </Cell>
      </Grid>
    </Block>
  );
}

GalleryDetail.propTypes = {
  actionLinkText: PropTypes.string,
  baseState: PropTypes.object,
  determineAddOnCharges: PropTypes.func,
  determinePrice: PropTypes.func,
  isSelectedVariantInStock: PropTypes.bool,
  product: PropTypes.object,
  renderMode: PropTypes.string,
  requiredFieldsBlank: PropTypes.bool,
  translate: PropTypes.func,
  assetChangedByVariantSelection: PropTypes.bool,
  category: PropTypes.string,
  currentAssetIndex: PropTypes.number,
  fetching: PropTypes.bool,
  processing: PropTypes.bool,
  inSession: PropTypes.bool,
  handleAddToCart: PropTypes.func,
  handleApplePayClick: PropTypes.func,
  getVariantData: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  pdpImageCropMethod: PropTypes.string,
  pdpImageShape: PropTypes.string,
  stripeConfig: PropTypes.object,
  poyntConfig: PropTypes.object,
  pdpLayout: PropTypes.string.isRequired,
  hasAddedToCartLabel: PropTypes.bool,
  isReverse: PropTypes.bool,
  ribbonStyle: PropTypes.object,
  ribbonText: PropTypes.string,
  productDetailClick: PropTypes.func
};

export default GalleryDetail;
